import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

const NavigationSidebar = ({ isCollapsed, onToggle, isMobileMenuOpen, setIsMobileMenuOpen }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { profile, signOut } = useAuth();
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  const handleSignOut = async () => {
    try {
      setIsLoggingOut(true);
      await signOut();
      navigate('/signin');
    } catch (error) {
      console.error('Error signing out:', error);
    } finally {
      setIsLoggingOut(false);
    }
  };

  // Close mobile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isMobileMenuOpen && !event?.target?.closest('.sidebar-container') && !event?.target?.closest('.mobile-menu-toggle')) {
        setIsMobileMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isMobileMenuOpen, setIsMobileMenuOpen]);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMobileMenuOpen]);

  const menuItems = [
    { id: 'dashboard', name: 'Dashboard', href: '/live-dashboard', icon: '📊' },
    { id: 'live-search', name: 'Live Search', href: '/live-search-results', icon: '🔍' },
    { id: 'mentions-feed', name: 'Mentions Feed', href: '/mentions-feed', icon: '💬' },
    { id: 'mention-details', name: 'Mention Details', href: '/mention-detail', icon: '📝' },
    { id: 'predictive-trends', name: 'Predictive Trends', href: '/predictive-trends', icon: '📈' },
    { id: 'persona-intelligence', name: 'Persona Intelligence', href: '/persona-intelligence', icon: '👥' },
    { id: 'competitor-analysis', name: 'Competitor Analysis', href: '/competitor-analysis', icon: '🎯' },
    { id: 'profile-settings', name: 'Profile Settings', href: '/profile', icon: '⚙️' },
    { id: 'integrations', name: 'Integrations', href: '/integrations', icon: '🔗' },
    { id: 'advanced-alerts', name: 'Alert Configuration', href: '/advanced-alerts-configuration', icon: '🔔' },
    { id: 'ai-feedback', name: 'AI Feedback', href: '/ai-accuracy-feedback-system', icon: '💡' },
  ];

  // Add admin panel link if user is admin
  if (profile?.role === 'admin') {
    menuItems?.push({ id: 'admin-panel', name: 'Admin Panel', href: '/admin', icon: '👑' });
  }

  const handleMenuItemClick = () => {
    // Close mobile menu when a menu item is clicked
    if (window.innerWidth < 1024) {
      setIsMobileMenuOpen(false);
    }
  };

  return (
    <>
      {/* Mobile Overlay with smooth fade */}
      {isMobileMenuOpen && (
        <div 
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40 lg:hidden transition-opacity duration-300 animate-fadeIn"
          onClick={() => setIsMobileMenuOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* Sidebar with smooth slide animation */}
      <aside 
        className={`sidebar-container fixed left-0 top-0 h-full w-64 bg-gray-900 border-r border-gray-800 flex flex-col z-50 transition-transform duration-300 ease-in-out shadow-2xl ${
          isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
        }`}
        role="navigation"
        aria-label="Main navigation"
      >
        {/* Logo Section - Improved spacing */}
        <div className="flex-shrink-0 p-4 sm:p-6 border-b border-gray-800 flex items-center justify-between">
          <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
            <img 
              src="/assets/images/whxi-1764685987080.png" 
              alt="HXi logo with modern circular and oval geometric shapes in shades of blue"
              className="h-8 sm:h-10 w-auto flex-shrink-0"
            />
            <span className="text-white font-bold text-lg sm:text-xl truncate">HXi Monitor</span>
          </div>
          {/* Close button for mobile - improved tap target */}
          <button
            onClick={() => setIsMobileMenuOpen(false)}
            className="lg:hidden text-gray-400 hover:text-white p-2 rounded-lg hover:bg-gray-800 transition-colors flex-shrink-0 ml-2"
            aria-label="Close menu"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* User Profile Section - Improved mobile layout */}
        {profile && (
          <div className="flex-shrink-0 p-3 sm:p-4 border-b border-gray-800">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center text-white font-semibold flex-shrink-0">
                {profile?.full_name?.charAt(0)?.toUpperCase() || profile?.email?.charAt(0)?.toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-white font-medium text-sm truncate">{profile?.full_name}</div>
                <div className="text-gray-400 text-xs truncate">{profile?.email}</div>
              </div>
            </div>
            {profile?.role === 'admin' && (
              <div className="mt-2">
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-500/20 text-purple-400">
                  👑 Admin
                </span>
              </div>
            )}
          </div>
        )}

        {/* Navigation Menu - Optimized scrolling */}
        <nav className="flex-1 p-3 sm:p-4 overflow-y-auto overscroll-contain" style={{ WebkitOverflowScrolling: 'touch' }}>
          <div className="space-y-1">
            {menuItems?.map((item) => {
              const isActive = location?.pathname === item?.href;
              return (
                <Link
                  key={item?.id}
                  to={item?.href}
                  onClick={handleMenuItemClick}
                  className={`flex items-center gap-3 px-3 sm:px-4 py-2.5 sm:py-3 rounded-lg transition-all duration-200 text-sm sm:text-base ${
                    isActive
                      ? 'bg-blue-600 text-white shadow-lg shadow-blue-600/20'
                      : 'text-gray-400 hover:bg-gray-800 hover:text-white active:bg-gray-700'
                  }`}
                >
                  <span className="text-lg sm:text-xl flex-shrink-0">{item?.icon}</span>
                  <span className="font-medium truncate">{item?.name}</span>
                </Link>
              );
            })}
          </div>
        </nav>

        {/* Sign Out Button - Improved spacing */}
        <div className="flex-shrink-0 p-3 sm:p-4 border-t border-gray-800">
          <button
            onClick={handleSignOut}
            disabled={isLoggingOut}
            className="w-full flex items-center gap-3 px-3 sm:px-4 py-2.5 sm:py-3 rounded-lg text-gray-400 hover:bg-gray-800 hover:text-white transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed active:bg-gray-700 text-sm sm:text-base"
          >
            <span className="text-lg sm:text-xl flex-shrink-0">🚪</span>
            <span className="font-medium">{isLoggingOut ? 'Signing out...' : 'Sign Out'}</span>
          </button>
        </div>
      </aside>
    </>
  );
};

export default NavigationSidebar;